import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  Info,
  Loader2,
  Mic,
  MicOff,
  Paperclip,
  Phone,
  PhoneOff,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { cn } from '@/shared/lib/cn';
import { Alert } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import { SelectField } from '@/shared/ui/select';

import {
  addFieldReference,
  FIELD_REFERENCE_LIMIT,
  fieldReferenceOptions,
  fieldReviewReference,
} from '../field-reference-options';

import {
  FieldChatMarkdown,
  FieldConversationDate,
  FieldConversationTime,
} from './field-chat-markdown';
import './field-conversation.css';
import { FieldPersonaAvatar } from './field-persona-avatar';
import { FieldVoiceSession } from './field-voice-session';

import type { FieldPanelProps } from './field-types';
import type { useFieldVoice } from '../api/use-field-voice';
import type { FieldDestination } from '../field-overview';
import type { FieldActorId } from '@mio/shared';

interface Props extends FieldPanelProps {
  actorId: FieldActorId;
  unreadByActor?: Partial<Record<FieldActorId, number>>;
  onActorChange: (actor: FieldActorId) => void;
  onNavigate?: (destination: FieldDestination) => void;
  onVoiceReferencesChange?: (actor: FieldActorId, sourceIds: string[]) => void;
  voice: ReturnType<typeof useFieldVoice>;
  focusId?: string | undefined;
  focusRequest?: number;
  composeRequest?:
    { id: number; actorId: FieldActorId; text: string; sourceIds: string[] } | undefined;
}

function replyFailureReason(error: string | null) {
  if (!error) return 'Please retry to request a new response.';
  const reasons: Record<string, string> = {
    field_model_unavailable: 'Company chat is temporarily unavailable. Please try again.',
    field_operation_failed: 'The response was interrupted. Please try again.',
    field_cancelled: 'This response was cancelled.',
    job_recovery_exhausted: 'The response was interrupted. Please try again.',
    engagement_closed: 'This engagement has ended and no longer accepts new messages.',
  };
  if (reasons[error]) return reasons[error];
  if (
    /^[a-z]+(?:_[a-z0-9]+)+$/.test(error) ||
    /provider\/controller|provider is not configured/i.test(error)
  )
    return 'Company chat is temporarily unavailable. Please try again.';
  return error;
}

export function FieldInbox({
  snapshot,
  busy,
  onAction,
  actorId,
  onActorChange,
  onNavigate,
  voice,
  unreadByActor,
  onVoiceReferencesChange,
  focusId,
  focusRequest,
  composeRequest,
}: Props) {
  const [drafts, setDrafts] = useState<Partial<Record<FieldActorId, string>>>({});
  const [references, setReferences] = useState<Partial<Record<FieldActorId, string[]>>>({});
  const [errors, setErrors] = useState<Partial<Record<FieldActorId, string>>>({});
  const [sendingTo, setSendingTo] = useState<FieldActorId | null>(null);
  const [showReferences, setShowReferences] = useState(false);
  const [showContext, setShowContext] = useState(false);
  const [showPeople, setShowPeople] = useState(false);
  const peoplePanel = useRef<HTMLElement>(null);
  const peopleTrigger = useRef<HTMLButtonElement>(null);
  const contextPanel = useRef<HTMLDivElement>(null);
  const contextTrigger = useRef<HTMLButtonElement>(null);
  const referencesPanel = useRef<HTMLDivElement>(null);
  const referencesTrigger = useRef<HTMLButtonElement>(null);
  const [newMessages, setNewMessages] = useState(false);
  const thread = useRef<HTMLDivElement>(null);
  const composer = useRef<HTMLTextAreaElement>(null);
  const sending = useRef(false);
  const composing = useRef(false);
  const pinned = useRef(true);
  const previousActor = useRef(actorId);
  const composedRequest = useRef<number | null>(null);
  useEffect(() => {
    if (!composeRequest || composedRequest.current === composeRequest.id) return;
    composedRequest.current = composeRequest.id;
    const { actorId: recipient, text, sourceIds } = composeRequest;
    setDrafts((previous) => {
      const existing = previous[recipient] ?? '';
      return {
        ...previous,
        [recipient]: existing.includes(text)
          ? existing
          : existing
            ? `${existing}\n\n${text}`
            : text,
      };
    });
    setReferences((previous) => ({
      ...previous,
      [recipient]: [...new Set([...(previous[recipient] ?? []), ...sourceIds])].slice(0, 8),
    }));
    composer.current?.focus();
  }, [composeRequest]);
  const actor = snapshot.actors.find((person) => person.id === actorId);
  const firstName = actor?.name.split(' ')[0] ?? 'your colleague';
  const messages = snapshot.messages.filter(
    (message) => message.actorId === actorId && !message.meetingId,
  );
  const draft = drafts[actorId] ?? '';
  const attachments = references[actorId] ?? [];
  const activeCall = voice.status !== 'off';
  const engagementActive = snapshot.enabled && snapshot.engagement.status === 'active';
  const canChat = engagementActive && snapshot.capabilities.chat;
  const lastMessage = messages.at(-1);
  const latestRequest = messages.findLast((message) => message.role === 'learner');
  const latestOperation = snapshot.operations.find(
    (operation) => operation.id === latestRequest?.operationId && operation.kind === 'turn',
  );
  const pendingReply = messages.some(
    (message) =>
      message.role === 'learner' &&
      snapshot.operations.some(
        (operation) =>
          operation.id === message.operationId &&
          operation.kind === 'turn' &&
          (operation.status === 'pending' || operation.status === 'running'),
      ),
  );
  const failedReply = latestOperation?.status === 'failed' ? latestOperation : null;
  const sendingHere = sendingTo === actorId;
  const sources = fieldReferenceOptions(snapshot);

  useEffect(() => {
    onVoiceReferencesChange?.(actorId, references[actorId] ?? []);
  }, [actorId, references, onVoiceReferencesChange]);

  useEffect(() => {
    function dismiss(event: PointerEvent | KeyboardEvent) {
      if (event instanceof KeyboardEvent) {
        if (event.key !== 'Escape') return;
        if (showContext) contextTrigger.current?.focus();
        else if (showReferences) referencesTrigger.current?.focus();
        else if (showPeople) peopleTrigger.current?.focus();
        setShowContext(false);
        setShowReferences(false);
        setShowPeople(false);
        return;
      }
      const target = event.target as HTMLElement;
      // The shared select renders its options in a portal outside the composer.
      if (target.closest('[role="listbox"], .ui-select-positioner')) return;
      if (!contextPanel.current?.contains(target) && !contextTrigger.current?.contains(target))
        setShowContext(false);
      if (
        !referencesPanel.current?.contains(target) &&
        !referencesTrigger.current?.contains(target)
      )
        setShowReferences(false);
      if (!peoplePanel.current?.contains(target) && !peopleTrigger.current?.contains(target))
        setShowPeople(false);
    }
    document.addEventListener('pointerdown', dismiss);
    document.addEventListener('keydown', dismiss);
    return () => {
      document.removeEventListener('pointerdown', dismiss);
      document.removeEventListener('keydown', dismiss);
    };
  }, [showContext, showReferences, showPeople]);

  function selectColleague(nextActor: FieldActorId) {
    setShowPeople(false);
    if (nextActor === actorId) return;
    voice.stop();
    setShowReferences(false);
    setShowContext(false);
    onActorChange(nextActor);
  }

  useEffect(() => {
    const viewport = thread.current;
    if (!viewport) return;
    const changedActor = previousActor.current !== actorId;
    if (changedActor || pinned.current || lastMessage?.role === 'learner') {
      if (viewport.clientHeight > 0) viewport.scrollTop = viewport.scrollHeight;
      pinned.current = true;
      setNewMessages(false);
    } else {
      setNewMessages(true);
    }
    previousActor.current = actorId;
  }, [lastMessage?.id, lastMessage?.role, actorId, pendingReply]);

  useEffect(() => {
    const viewport = thread.current;
    if (!viewport || typeof ResizeObserver === 'undefined') return;
    // The inbox stays mounted while another workspace panel is selected. Wait
    // for real layout on reveal before restoring a conversation pinned to latest.
    const observer = new ResizeObserver(() => {
      if (viewport.clientHeight > 0 && pinned.current) {
        viewport.scrollTop = viewport.scrollHeight;
        setNewMessages(false);
      }
    });
    observer.observe(viewport);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const input = composer.current;
    if (!input) return;
    input.style.height = 'auto';
    input.style.height = draft ? `${Math.max(48, Math.min(input.scrollHeight, 128))}px` : '40px';
  }, [draft, actorId]);

  useEffect(() => {
    if (!focusId) return;
    // Evidence links must stay on the cited message when the panel is revealed,
    // instead of being scrolled back to the latest reply by ResizeObserver.
    pinned.current = false;
    const frame = requestAnimationFrame(() => {
      const message = Array.from(
        thread.current?.querySelectorAll<HTMLElement>('[data-message-id]') ?? [],
      ).find((element) => element.dataset.messageId === focusId);
      message?.focus({ preventScroll: true });
      message?.scrollIntoView?.({ block: 'center' });
    });
    return () => cancelAnimationFrame(frame);
  }, [focusId, focusRequest, actorId]);

  async function sendMessage() {
    const text = draft;
    if (sending.current || busy || !canChat || !text.trim()) return;
    // A ref closes the gap before React renders the disabled button.
    sending.current = true;
    setSendingTo(actorId);
    setErrors((previous) => ({ ...previous, [actorId]: '' }));
    try {
      const sent = await onAction({
        type: 'message',
        actorId,
        text: text.trim(),
        sourceIds: attachments,
        modality: 'chat',
      });
      if (sent) {
        setDrafts((previous) => ({
          ...previous,
          [actorId]: previous[actorId] === draft ? '' : (previous[actorId] ?? ''),
        }));
        setReferences((previous) => ({ ...previous, [actorId]: [] }));
        pinned.current = true;
      } else {
        setErrors((previous) => ({
          ...previous,
          [actorId]: 'Message could not be sent. Your draft is saved; try again.',
        }));
      }
    } catch {
      setErrors((previous) => ({
        ...previous,
        [actorId]: 'Message could not be sent. Your draft is saved; try again.',
      }));
    } finally {
      sending.current = false;
      setSendingTo(null);
    }
  }

  async function retryReply() {
    if (!failedReply || sending.current || busy || !canChat || pendingReply) return;
    sending.current = true;
    setSendingTo(actorId);
    setErrors((previous) => ({ ...previous, [actorId]: '' }));
    try {
      const queued = await onAction({ type: 'operation.retry', operationId: failedReply.id });
      if (queued) pinned.current = true;
      else
        setErrors((previous) => ({
          ...previous,
          [actorId]: 'The reply could not be retried. Your original request and draft are saved.',
        }));
    } catch {
      setErrors((previous) => ({
        ...previous,
        [actorId]: 'The reply could not be retried. Your original request and draft are saved.',
      }));
    } finally {
      sending.current = false;
      setSendingTo(null);
    }
  }

  return (
    <div className="field-conversations">
      <div className="field-conversation-layout">
        <nav
          ref={peoplePanel}
          id="field-colleague-roster"
          className={cn('field-people-roster', showPeople && 'is-open')}
          aria-label="Company colleagues"
        >
          <div className="field-people-heading" data-field-tour="colleague-directory">
            <span>Direct messages</span>
            <span>{snapshot.actors.length}</span>
          </div>
          <div className="field-people-list">
            {snapshot.actors.map((person) => {
              const latest = snapshot.messages.findLast(
                (message) => message.actorId === person.id && !message.meetingId,
              );
              const preview = latest?.text
                .replace(/[#*_`>]/g, '')
                .replace(/\s+/g, ' ')
                .trim();
              return (
                <button
                  type="button"
                  key={person.id}
                  onClick={() => selectColleague(person.id)}
                  aria-label={`${person.name}, ${person.role}`}
                  aria-pressed={actorId === person.id}
                  aria-describedby={
                    unreadByActor?.[person.id] ? `field-unread-${person.id}` : undefined
                  }
                  className={cn('field-person', actorId === person.id && 'is-selected')}
                >
                  <FieldPersonaAvatar
                    actorId={person.id}
                    name={person.name}
                    className="field-person-avatar"
                  />
                  <span className="field-person-content">
                    <span className="field-person-name">
                      {person.name}
                      {unreadByActor?.[person.id] ? (
                        <span
                          className="field-person-unread"
                          id={`field-unread-${person.id}`}
                          aria-label={`${unreadByActor[person.id]} unread on this device`}
                        >
                          {unreadByActor[person.id]}
                        </span>
                      ) : null}
                    </span>
                    <span className="field-person-role">{person.role}</span>
                    <span className={cn('field-person-preview', drafts[person.id] && 'has-draft')}>
                      {drafts[person.id]
                        ? `Draft: ${drafts[person.id]}`
                        : preview
                          ? `${latest?.role === 'learner' ? 'You: ' : ''}${preview}`
                          : person.focus}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </nav>
        <section
          className="field-conversation-main"
          aria-label={`Conversation with ${actor?.name ?? actorId}`}
        >
          <header className="field-conversation-header">
            <div className="field-conversation-identity" data-field-tour="colleague-identity">
              <FieldPersonaAvatar
                actorId={actorId}
                name={actor?.name ?? actorId}
                className="field-person-avatar"
              />
              <div className="min-w-0">
                <h2 className="field-conversation-title">
                  <span className="field-colleague-name">{actor?.name ?? actorId}</span>
                  <button
                    type="button"
                    className="field-people-compact"
                    ref={peopleTrigger}
                    aria-label="Change colleague"
                    aria-expanded={showPeople}
                    aria-controls="field-colleague-roster"
                    title="Change colleague"
                    onClick={() => setShowPeople((value) => !value)}
                  >
                    <span>{actor?.name ?? actorId}</span>
                    <ChevronDown className="size-3.5" aria-hidden="true" />
                  </button>
                </h2>
                <p className="text-fg-muted mt-0.5 text-xs">{actor?.role}</p>
              </div>
            </div>
            <div className="field-conversation-actions">
              <Button
                variant="ghost"
                size="sm"
                ref={contextTrigger}
                aria-label="About this colleague"
                aria-controls="field-colleague-context"
                aria-expanded={showContext}
                onClick={() => setShowContext((value) => !value)}
              >
                <Info className="size-3.5" aria-hidden="true" />
                About
              </Button>
              {activeCall ? (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={voice.toggleMute}
                    aria-label={voice.muted ? 'Unmute microphone' : 'Mute microphone'}
                    aria-pressed={voice.muted}
                    disabled={voice.status === 'connecting'}
                  >
                    {voice.muted ? (
                      <MicOff className="size-4" aria-hidden="true" />
                    ) : (
                      <Mic className="size-4" aria-hidden="true" />
                    )}
                    {voice.muted ? 'Unmute' : 'Mute'}
                  </Button>
                  <Button variant="secondary" size="sm" onClick={voice.stop}>
                    <PhoneOff className="size-3.5" aria-hidden="true" />
                    End call
                  </Button>
                </>
              ) : (
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={busy || !snapshot.capabilities.voice || !canChat}
                  onClick={() => void voice.start()}
                  aria-label={`Call ${actor?.name ?? firstName}`}
                  data-field-tour="voice-call"
                  title={
                    !snapshot.capabilities.voice
                      ? 'Voice is not enabled for this engagement.'
                      : `Call ${firstName}`
                  }
                >
                  <Phone className="size-3.5" aria-hidden="true" />
                  Call
                </Button>
              )}
            </div>
          </header>
          {showContext ? (
            <div
              ref={contextPanel}
              id="field-colleague-context"
              className="field-colleague-context"
              role="dialog"
              aria-label={`About ${actor?.name ?? 'this colleague'}`}
            >
              <div className="flex items-start justify-between gap-3">
                <p className="font-semibold">
                  {actor?.name} · {actor?.role}
                </p>
                <button
                  type="button"
                  className="field-reference-remove"
                  aria-label="Close colleague details"
                  onClick={() => {
                    setShowContext(false);
                    contextTrigger.current?.focus();
                  }}
                >
                  <X className="size-4" aria-hidden="true" />
                </button>
              </div>
              <p className="mt-2">{actor?.focus}</p>
              <p className="text-fg-muted mt-2">
                Only messages and references you share reach this colleague. Your private Mio
                conversation stays separate.
              </p>
            </div>
          ) : null}
          <FieldVoiceSession voice={voice} colleagueName={actor?.name ?? firstName} />
          <div className="field-thread-shell">
            <div
              ref={thread}
              className="field-conversation-history"
              data-field-tour="review-messages"
              role="region"
              aria-label="Conversation history"
              tabIndex={0}
              onScroll={(event) => {
                const viewport = event.currentTarget;
                if (viewport.clientHeight === 0) return;
                pinned.current =
                  viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight < 72;
                if (pinned.current) setNewMessages(false);
              }}
            >
              {messages.length === 0 ? (
                <div className="field-conversation-empty">
                  <span className="field-empty-eyebrow">Direct conversation</span>
                  <h3>Work with {firstName}</h3>
                  <p>{actor?.focus ?? 'Ask a question and share the work you want to discuss.'}</p>
                  <p>
                    Ask for context, discuss a decision, or attach work for feedback. Only what you
                    send is shared.
                  </p>
                  <button
                    type="button"
                    disabled={!canChat}
                    onClick={() => {
                      setDrafts((previous) => ({
                        ...previous,
                        [actorId]: 'What should I focus on first?',
                      }));
                      composer.current?.focus();
                    }}
                  >
                    Draft an opening question <ArrowUp className="size-3.5" aria-hidden="true" />
                  </button>
                </div>
              ) : (
                <ol className="field-message-list" aria-label="Conversation messages">
                  {messages.map((message, index) => (
                    <li key={message.id} className="min-w-0">
                      <FieldConversationDate
                        value={message.createdAt}
                        previous={messages[index - 1]?.createdAt}
                      />
                      <article
                        className={cn('field-message', `field-message--${message.role}`)}
                        tabIndex={-1}
                        data-message-id={message.id}
                        data-focused={focusId === message.id}
                      >
                        {message.role === 'colleague' ? (
                          <FieldPersonaAvatar
                            actorId={message.actorId}
                            name={actor?.name ?? message.actorId}
                            className="field-message-avatar"
                          />
                        ) : (
                          <span className="field-message-avatar" aria-hidden="true">
                            {message.role === 'learner' ? 'Y' : '·'}
                          </span>
                        )}
                        <div className="field-message-body">
                          <div className="field-message-meta">
                            <span className="field-message-author">
                              {message.role === 'learner'
                                ? 'You'
                                : message.role === 'system'
                                  ? 'Project update'
                                  : actor?.name}
                            </span>
                            <FieldConversationTime value={message.createdAt} />
                          </div>
                          {message.role === 'learner' ? (
                            <p className="field-message-text">{message.text}</p>
                          ) : (
                            <FieldChatMarkdown
                              source={message.text}
                              snapshot={snapshot}
                              onNavigate={onNavigate}
                            />
                          )}
                          {message.sourceIds.length ? (
                            <div
                              className="field-message-references"
                              aria-label="Shared references"
                            >
                              {message.sourceIds.map((id) => {
                                const reviewReference = fieldReviewReference(snapshot, id);
                                const decision = snapshot.discovery?.decisions.find(
                                  (item) => item.id === id,
                                );
                                const destination =
                                  reviewReference?.destination ??
                                  (decision
                                    ? { section: 'engagement' as const, focusId: decision.id }
                                    : undefined);
                                const label =
                                  reviewReference?.label ??
                                  (decision
                                    ? `Understanding · ${decision.title} · v${decision.version}`
                                    : undefined) ??
                                  sources.find((source) => source.id === id)?.label ??
                                  (id.startsWith('review-') ? 'Review unavailable' : id);
                                const content = (
                                  <>
                                    <Paperclip className="size-3 shrink-0" aria-hidden="true" />
                                    <span>{label}</span>
                                  </>
                                );
                                return destination && onNavigate ? (
                                  <button
                                    key={id}
                                    type="button"
                                    className="field-reference field-reference-link"
                                    title={label}
                                    onClick={() => onNavigate(destination)}
                                  >
                                    {content}
                                  </button>
                                ) : (
                                  <span key={id} className="field-reference" title={label}>
                                    {content}
                                  </span>
                                );
                              })}
                            </div>
                          ) : null}
                        </div>
                      </article>
                    </li>
                  ))}
                </ol>
              )}
              {pendingReply && !activeCall ? (
                <div role="status" className="text-fg-muted mt-5 flex items-center gap-2 text-xs">
                  <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
                  <span>{firstName} is replying…</span>
                </div>
              ) : null}
              {failedReply ? (
                <div className="mt-4">
                  <Alert>
                    <p>{firstName} couldn’t reply. Your message is saved.</p>
                    <p className="mt-1 text-xs">{replyFailureReason(failedReply.error)}</p>
                    <p className="mt-1 text-xs">
                      Retry resumes this saved request without sending another message.
                    </p>
                    <button
                      type="button"
                      className="mt-2 text-xs font-medium underline underline-offset-2"
                      disabled={busy || !!sendingTo || !canChat || pendingReply}
                      onClick={() => void retryReply()}
                    >
                      Retry reply
                    </button>
                  </Alert>
                </div>
              ) : null}
            </div>
            {newMessages ? (
              <button
                type="button"
                className="field-new-messages"
                onClick={() => {
                  if (thread.current) thread.current.scrollTop = thread.current.scrollHeight;
                  pinned.current = true;
                  setNewMessages(false);
                }}
              >
                <ArrowDown className="size-3" aria-hidden="true" />
                New messages
              </button>
            ) : null}
          </div>
          <div className="field-message-composer">
            {voice.error || voice.unsent || errors[actorId] ? (
              <div className="mb-2 max-h-28 space-y-2 overflow-y-auto">
                {errors[actorId] ? <Alert>{errors[actorId]}</Alert> : null}
                {voice.error ? <Alert>{voice.error}</Alert> : null}
                {voice.unsent ? (
                  <Alert tone="info">
                    Speech was not sent: “{voice.unsent}”{' '}
                    <button
                      type="button"
                      className="underline"
                      onClick={() => {
                        setDrafts((previous) => ({
                          ...previous,
                          [actorId]: `${previous[actorId] ?? ''} ${voice.unsent}`.trim(),
                        }));
                        voice.clearUnsent();
                        composer.current?.focus();
                      }}
                    >
                      Move to draft
                    </button>
                  </Alert>
                ) : null}
              </div>
            ) : null}
            <form
              className="field-composer-form"
              onSubmit={(event) => {
                event.preventDefault();
                void sendMessage();
              }}
              aria-label={`Message ${firstName}`}
            >
              <label htmlFor="field-message" className="field-composer-label">
                Message {actor?.name}
              </label>
              <textarea
                ref={composer}
                data-field-tour="message-composer"
                id="field-message"
                className="field-composer-input"
                rows={2}
                maxLength={12000}
                value={draft}
                onChange={(event) =>
                  setDrafts((previous) => ({ ...previous, [actorId]: event.target.value }))
                }
                onCompositionStart={() => {
                  composing.current = true;
                }}
                onCompositionEnd={() => {
                  composing.current = false;
                }}
                onKeyDown={(event) => {
                  if (
                    event.key === 'Enter' &&
                    !event.shiftKey &&
                    !event.nativeEvent.isComposing &&
                    !composing.current &&
                    event.keyCode !== 229
                  ) {
                    event.preventDefault();
                    void sendMessage();
                  }
                }}
                placeholder={
                  canChat
                    ? `Message ${firstName}…`
                    : engagementActive
                      ? 'Company chat is unavailable'
                      : 'This engagement is closed'
                }
                disabled={!canChat}
                aria-describedby="field-composer-help"
              />
              {attachments.length ? (
                <div
                  className="flex max-h-28 flex-wrap gap-1 overflow-y-auto px-3 pb-2"
                  aria-label="Attached references"
                >
                  {attachments.map((id) => (
                    <span
                      key={id}
                      className="field-reference"
                      title={sources.find((source) => source.id === id)?.label ?? id}
                    >
                      <Paperclip className="size-3 shrink-0" aria-hidden="true" />
                      <span className="field-reference-label">
                        {sources.find((source) => source.id === id)?.label ?? id}
                      </span>
                      <button
                        type="button"
                        aria-label={`Remove reference: ${sources.find((source) => source.id === id)?.label ?? id}`}
                        className="field-reference-remove"
                        disabled={!!sendingTo}
                        onClick={() =>
                          setReferences((previous) => ({
                            ...previous,
                            [actorId]: (previous[actorId] ?? []).filter(
                              (reference) => reference !== id,
                            ),
                          }))
                        }
                      >
                        <X className="size-3" aria-hidden="true" />
                      </button>
                    </span>
                  ))}
                </div>
              ) : null}
              {showReferences ? (
                <div
                  ref={referencesPanel}
                  id="field-reference-picker"
                  className="field-reference-picker"
                >
                  <label
                    htmlFor="field-message-source"
                    className="text-fg-muted mb-1 block text-xs"
                  >
                    Share a reference with {firstName}
                  </label>
                  <SelectField
                    id="field-message-source"
                    finalFocus={() => {
                      setShowReferences(false);
                      return composer.current;
                    }}
                    value=""
                    disabled={
                      busy || !!sendingTo || !canChat || attachments.length >= FIELD_REFERENCE_LIMIT
                    }
                    onValueChange={(nextValue) => {
                      if (!nextValue) return;
                      setReferences((previous) => {
                        const selected = previous[actorId] ?? [];
                        return { ...previous, [actorId]: addFieldReference(selected, nextValue) };
                      });
                    }}
                    options={[
                      {
                        value: '',
                        label: sources.length
                          ? 'Choose a ticket, pull request, document, or run'
                          : 'No references available yet',
                      },
                      ...sources.map((source) => ({
                        value: source.id,
                        label: source.label,
                        disabled: attachments.includes(source.id),
                      })),
                    ]}
                  />
                </div>
              ) : null}
              <div className="field-composer-toolbar">
                <button
                  ref={referencesTrigger}
                  data-field-tour="message-references"
                  type="button"
                  className="field-attach-button"
                  aria-controls="field-reference-picker"
                  aria-label="Attach a reference"
                  aria-expanded={showReferences}
                  disabled={!canChat}
                  onClick={() => setShowReferences((value) => !value)}
                >
                  <Paperclip className="size-3.5" aria-hidden="true" />
                  <span>Attach reference</span>
                  <ChevronDown className="size-3" aria-hidden="true" />
                </button>
                <div className="flex items-center gap-2">
                  {sendingHere ? (
                    <span className="text-fg-muted text-xs" role="status">
                      Sending…
                    </span>
                  ) : null}
                  <Button
                    type="submit"
                    size="sm"
                    className="field-send-button"
                    disabled={busy || !!sendingTo || !draft.trim() || !canChat}
                    aria-label="Send message"
                    title="Send message (Enter)"
                  >
                    {sendingHere ? (
                      <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                    ) : (
                      <ArrowUp className="size-4" aria-hidden="true" />
                    )}
                    Send
                  </Button>
                </div>
              </div>
            </form>
            <div className="field-message-help" id="field-composer-help">
              <span>Enter to send · Shift + Enter for a new line</span>
              {attachments.length ? (
                <p>
                  {attachments.length}/{FIELD_REFERENCE_LIMIT} references attached.{' '}
                  {attachments.length >= FIELD_REFERENCE_LIMIT
                    ? 'Remove a reference to attach another.'
                    : 'Add more with Attach reference.'}{' '}
                  Shared when you send or speak on the call.
                </p>
              ) : null}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
